import AccountBalanceRounded from '@mui/icons-material/AccountBalanceRounded';
import Inventory2Rounded from '@mui/icons-material/Inventory2Rounded';
import PlaylistAddRounded from '@mui/icons-material/PlaylistAddRounded';
import SavingsRounded from '@mui/icons-material/SavingsRounded';
import WarehouseRounded from '@mui/icons-material/WarehouseRounded';
import TuneRounded from '@mui/icons-material/TuneRounded';
import { Box, Button, FormControlLabel, Switch, Typography } from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { api, unwrap } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { DataTable, SearchField, type Column } from '../../components/DataTable';
import { Money, Qty } from '../../components/Money';
import { EmptyState } from '../../components/states';
import { moneyCompact, qty } from '../../lib/format';
import { useShop } from '../../shop/ShopProvider';
import { InventoryLayout } from './InventoryLayout';
import { inventoryKeys, useLocations, type StockBalance } from './model';
import { LocationSelect, StatCard, StatGrid } from './shared';

export function StockOnHandPage() {
  const { can } = useAuth();
  const { format } = useShop();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [locationId, setLocationId] = useState('');
  const [includeZero, setIncludeZero] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const seesCost = can('products.view_cost');

  const query = {
    q: q || undefined,
    locationId: locationId || undefined,
    includeZero,
    page,
    pageSize,
  };
  const balances = useQuery({
    queryKey: inventoryKeys.balances(query),
    queryFn: () =>
      unwrap(
        api.GET('/stock/balances', {
          params: {
            query: { ...query, includeZero: includeZero ? 'true' : 'false' },
          },
        }),
      ),
    placeholderData: keepPreviousData,
  });
  const valuation = useQuery({
    queryKey: inventoryKeys.valuation,
    queryFn: () => unwrap(api.GET('/stock/valuation')),
    enabled: seesCost,
  });
  const locations = useLocations();
  const activeLocations = locations.data?.filter((l) => l.isActive) ?? [];
  const selectedValue = locationId
    ? valuation.data?.locations.find((l) => l.locationId === locationId)?.value
    : valuation.data?.total;

  const columns: Column<StockBalance>[] = [
    {
      key: 'item',
      header: 'Item',
      render: (r) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {r.displayName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {r.sku}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'location',
      header: 'Location',
      hideBelow: 'sm',
      render: (r) => r.locationName,
    },
    {
      key: 'onHand',
      header: 'On hand',
      align: 'right',
      render: (r) => <Qty value={r.qtyOnHand} unit={r.unitSymbol} />,
    },
    {
      key: 'reserved',
      header: 'Reserved',
      align: 'right',
      hideBelow: 'md',
      render: (r) =>
        Number(r.qtyReserved) ? (
          <Qty value={r.qtyReserved} />
        ) : (
          <Typography component="span" color="text.disabled">
            —
          </Typography>
        ),
    },
    {
      key: 'available',
      header: 'Available',
      align: 'right',
      render: (r) => {
        const n = Number(r.available);
        return (
          <Box
            component="span"
            className="tabular"
            sx={{
              fontWeight: 650,
              px: 1,
              py: 0.25,
              borderRadius: '8px',
              whiteSpace: 'nowrap',
              color: n <= 0 ? 'error.main' : 'text.primary',
              bgcolor: n <= 0 ? 'color-mix(in srgb, currentColor 8%, transparent)' : undefined,
            }}
          >
            {qty(r.available, format)} {r.unitSymbol}
          </Box>
        );
      },
    },
    ...(seesCost
      ? ([
          {
            key: 'avgCost',
            header: 'Avg cost',
            align: 'right',
            hideBelow: 'lg',
            render: (r) => <Money value={r.avgCost} />,
          },
          {
            key: 'value',
            header: 'Value',
            align: 'right',
            hideBelow: 'sm',
            render: (r) => <Money value={r.value} strong />,
          },
        ] satisfies Column<StockBalance>[])
      : []),
  ];

  const filtered = Boolean(q || locationId || includeZero);

  return (
    <InventoryLayout
      actions={
        <>
          {can('inventory.count') && (
            <Button
              variant="outlined"
              startIcon={<PlaylistAddRounded />}
              onClick={() => void navigate('/inventory/counts')}
            >
              Count stock
            </Button>
          )}
          {can('inventory.adjust') && (
            <Button
              variant="contained"
              startIcon={<TuneRounded />}
              onClick={() => void navigate('/inventory/adjustments/new')}
            >
              Adjust stock
            </Button>
          )}
        </>
      }
    >
      <StatGrid>
        {seesCost && (
          <StatCard
            label={locationId ? 'Stock value here' : 'Stock value'}
            value={moneyCompact(selectedValue, format)}
            hint="At average cost"
            icon={<SavingsRounded />}
            tone="success"
            loading={valuation.isLoading}
          />
        )}
        <StatCard
          label="Stock lines"
          value={balances.data ? balances.data.page.total.toLocaleString() : '—'}
          hint={includeZero ? 'Including zero balances' : 'Items with stock'}
          icon={<Inventory2Rounded />}
          loading={balances.isLoading}
        />
        <StatCard
          label="Locations"
          value={activeLocations.length}
          hint={`${String(activeLocations.filter((l) => l.isSellable).length)} sellable`}
          icon={<WarehouseRounded />}
          tone="info"
          loading={locations.isLoading}
        />
        {seesCost && (
          <StatCard
            label="Inventory account"
            value={moneyCompact(valuation.data?.inventoryAccountBalance, format)}
            hint={
              valuation.data && Number(valuation.data.difference) !== 0
                ? `Difference ${moneyCompact(valuation.data.difference, format)}`
                : 'Matches stock value'
            }
            icon={<AccountBalanceRounded />}
            tone={valuation.data && Number(valuation.data.difference) !== 0 ? 'warning' : 'primary'}
            loading={valuation.isLoading}
          />
        )}
      </StatGrid>

      <DataTable
        columns={columns}
        rows={balances.data?.data}
        rowKey={(r) => `${r.variantId}:${r.locationId}`}
        loading={balances.isLoading}
        page={balances.data?.page}
        onPageChange={setPage}
        onPageSizeChange={(s) => {
          setPageSize(s);
          setPage(1);
        }}
        onRowClick={(r) =>
          void navigate(
            `/inventory/movements?${new URLSearchParams({
              variantId: r.variantId,
              item: `${r.displayName} · ${r.sku}`,
              locationId: r.locationId,
            }).toString()}`,
          )
        }
        toolbar={
          <>
            <SearchField
              value={q}
              onChange={(v) => {
                setQ(v);
                setPage(1);
              }}
              placeholder="Search name, SKU or barcode"
            />
            <LocationSelect
              value={locationId}
              onChange={(v) => {
                setLocationId(v);
                setPage(1);
              }}
              allLabel="All locations"
              size="small"
            />
            <Box sx={{ flex: 1 }} />
            <FormControlLabel
              control={
                <Switch
                  checked={includeZero}
                  onChange={(e) => {
                    setIncludeZero(e.target.checked);
                    setPage(1);
                  }}
                />
              }
              label="Show zero stock"
              sx={{ whiteSpace: 'nowrap', flexShrink: 0, mr: 0 }}
            />
          </>
        }
        empty={
          filtered ? (
            <EmptyState
              icon={<Inventory2Rounded />}
              title="Nothing matches"
              body="No stock matches these filters. Try another search or location."
            />
          ) : (
            <EmptyState
              icon={<Inventory2Rounded />}
              title="No stock yet"
              body="Record opening stock for the items you already have, or receive a purchase to start tracking stock."
              action={
                can('inventory.opening_stock')
                  ? {
                      label: 'Enter opening stock',
                      onClick: () => void navigate('/inventory/opening'),
                    }
                  : undefined
              }
            />
          )
        }
      />
    </InventoryLayout>
  );
}
